import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { fmtDate, getPlatformSettings } from '../utils/api';
import ResultCheckerModal from '../components/ResultCheckerModal';
import { IcMapPin, IcPhone, IcWhatsApp, IcMail } from '../components/Icons';
import '../styles/homepage.css';

/* ─── scroll reveal ─────────────────────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); io.unobserve(e.target); } });
    }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });

    const observe = (root) => {
      root.querySelectorAll('[data-reveal]:not(.revealed)').forEach(el => io.observe(el));
    };

    // observe elements already in the DOM
    observe(document);

    // watch for new elements added by async data (team, gallery, etc.)
    const mo = new MutationObserver(() => observe(document));
    mo.observe(document.body, { childList: true, subtree: true });

    return () => { io.disconnect(); mo.disconnect(); };
  }, []);
}

/* ─── typewriter hook ───────────────────────────────────────────────────── */
function useTypewriter(phrases, { typeSpeed = 85, deleteSpeed = 42, pauseAfter = 2600, pauseBefore = 500 } = {}) {
  const [display, setDisplay] = useState('');
  const [phaseIdx, setPhaseIdx] = useState(0); // which phrase index
  const iRef   = useRef(0);
  const dirRef = useRef('type');
  const charRef = useRef(0);
  const timRef  = useRef(null);

  useEffect(() => {
    // Respect reduced-motion: show the first phrase statically, no animation
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(phrases[0]);
      return;
    }
    const tick = () => {
      const phrase = phrases[iRef.current % phrases.length];
      if (dirRef.current === 'type') {
        charRef.current += 1;
        setDisplay(phrase.slice(0, charRef.current));
        if (charRef.current >= phrase.length) {
          dirRef.current = 'pause';
          timRef.current = setTimeout(tick, pauseAfter);
          return;
        }
        timRef.current = setTimeout(tick, typeSpeed + Math.random() * 30);
      } else if (dirRef.current === 'pause') {
        dirRef.current = 'delete';
        timRef.current = setTimeout(tick, deleteSpeed);
      } else if (dirRef.current === 'delete') {
        charRef.current -= 1;
        setDisplay(phrase.slice(0, charRef.current));
        if (charRef.current <= 0) {
          iRef.current += 1;
          setPhaseIdx(iRef.current);
          dirRef.current = 'wait';
          timRef.current = setTimeout(tick, pauseBefore);
          return;
        }
        timRef.current = setTimeout(tick, deleteSpeed - 6);
      } else {
        dirRef.current = 'type';
        timRef.current = setTimeout(tick, typeSpeed);
      }
    };
    timRef.current = setTimeout(tick, 600);
    return () => clearTimeout(timRef.current);
  }, []); // eslint-disable-line

  return { display, phaseIdx };
}

/* ─── count-up hook ─────────────────────────────────────────────────────── */
function useCountUp(numStr, active) {
  const num    = parseInt(numStr) || 0;
  const suffix = numStr.replace(/[0-9]/g, '');
  const [val, setVal] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    if (!active || done.current) return;
    done.current = true;
    let raf;
    const start = performance.now();
    const dur = 1800;
    const tick = (now) => {
      const t    = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setVal(Math.floor(ease * num));
      if (t < 1) raf = requestAnimationFrame(tick); else setVal(num);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return active ? `${val}${suffix}` : numStr;
}

/* ─── stat ring ─────────────────────────────────────────────────────────── */
function StatRing({ num, label, fillPercent, index }) {
  const ref    = useRef();
  const rafRef = useRef(null);
  const [active,   setActive]   = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setActive(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const delay = index * 160;
    const timer = setTimeout(() => {
      const start = performance.now();
      const dur   = 1600;
      const tick  = (now) => {
        const t    = Math.min((now - start) / dur, 1);
        const ease = 1 - Math.pow(1 - t, 3);
        setProgress(ease * fillPercent);
        if (t < 1) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(timer); cancelAnimationFrame(rafRef.current); };
  }, [active, fillPercent, index]);

  const display       = useCountUp(num, active);
  const R             = 44;
  const circumference = 2 * Math.PI * R;
  const offset        = circumference * (1 - progress / 100);

  return (
    <div ref={ref} className="hp2-stat-ring">
      <div className="hp2-stat-ring-wrap">
        <svg width={110} height={110} viewBox="0 0 110 110"
          style={{ transform: 'rotate(-90deg)', display: 'block', overflow: 'visible' }}>
          <defs>
            <filter id={`glow-${index}`} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          <circle cx={55} cy={55} r={R} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={9} />
          <circle cx={55} cy={55} r={R} fill="none" stroke="#FFA500" strokeWidth={9}
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" filter={`url(#glow-${index})`} />
        </svg>
        <div className="hp2-stat-ring-center">
          <span className="hp2-stat-ring-num">{display}</span>
        </div>
      </div>
      <span className="hp2-stat-ring-label">{label}</span>
    </div>
  );
}

/* ─── FAQ accordion ─────────────────────────────────────────────────────── */
// Mounts children only when scrolled near the viewport — keeps heavy
// below-the-fold sections (map, org chart) out of the initial render.
function Deferred({ children, minHeight = 300 }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!('IntersectionObserver' in window)) { setShow(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShow(true); io.disconnect(); }
    }, { rootMargin: '400px' });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} style={show ? undefined : { minHeight }}>{show ? children : null}</div>;
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hp2-faq-item${open ? ' open' : ''}`}>
      <button className="hp2-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <svg className="hp2-faq-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && <div className="hp2-faq-a">{a}</div>}
    </div>
  );
}

/* ─── world map (Ibadan-only blink) ─────────────────────────────────────── */
function WorldMap() {
  const mapRef = useRef(null);

  useEffect(() => {
    const loadScript = (src) =>
      new Promise((res, rej) => {
        if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
        const s = document.createElement('script');
        s.src = src; s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });

    (async () => {
      try {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js');
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/topojson/3.0.2/topojson.min.js');

        const container = mapRef.current;
        if (!container) return;
        const W = container.offsetWidth || 900;
        const H = Math.min(300, Math.max(180, W * 0.32));
        container.innerHTML = '';

        const svg = window.d3.select(container).append('svg')
          .attr('width', '100%').attr('height', H).style('display', 'block');

        svg.append('rect').attr('width', W).attr('height', H).attr('fill', '#f8f5f0');

        const projection = window.d3.geoNaturalEarth1()
          .scale(W / 6.2).translate([W / 2, H / 2 + 8]);
        const path = window.d3.geoPath().projection(projection);

        const world = await window.d3.json('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json');
        const countries = window.topojson.feature(world, world.objects.countries);

        svg.append('g').selectAll('path').data(countries.features).join('path')
          .attr('d', path).attr('fill', '#ddd6cc').attr('stroke', '#c8c0b4').attr('stroke-width', 0.4);

        const staticDots = [
          [-0.1,51.5],[-74.0,40.7],[3.4,6.5],[36.8,-1.3],[-0.2,5.6],
          [-79.4,43.7],[2.35,48.85],[55.3,25.2],[103.8,1.35],[151.2,-33.9],[18.4,-33.9],
        ];
        staticDots.forEach(([lon, lat]) => {
          const [px, py] = projection([lon, lat]);
          if (!px || !py) return;
          svg.append('circle').attr('cx', px).attr('cy', py).attr('r', 2.8)
            .attr('fill', '#a8a099').attr('opacity', 0.55);
        });

        const ibadan = projection([3.9, 7.4]);
        const connTargets = [[-0.1,51.5],[-74.0,40.7],[36.8,-1.3],[3.4,6.5],[-0.2,5.6]];
        connTargets.forEach(([lon, lat]) => {
          const [bx, by] = projection([lon, lat]);
          if (!bx || !by) return;
          const mx = (ibadan[0] + bx) / 2;
          const my = (ibadan[1] + by) / 2 - Math.hypot(bx - ibadan[0], by - ibadan[1]) * 0.22;
          svg.append('path').attr('d', `M${ibadan[0]},${ibadan[1]} Q${mx},${my} ${bx},${by}`)
            .attr('fill', 'none').attr('stroke', '#c98240').attr('stroke-width', 0.65)
            .attr('stroke-dasharray', '3 5').attr('opacity', 0.35);
        });

        if (!document.getElementById('ibadan-pulse-css')) {
          const st = document.createElement('style');
          st.id = 'ibadan-pulse-css';
          st.textContent = `
            @keyframes ib-pulse { 0%,100%{opacity:.9} 50%{opacity:.25} }
            @keyframes ib-rip   { 0%{r:5px;opacity:.5;stroke-width:1.5} 100%{r:22px;opacity:0;stroke-width:.3} }
            .ib-rip1 { animation: ib-rip 2.4s ease-out infinite; }
            .ib-rip2 { animation: ib-rip 2.4s ease-out infinite 1.2s; }
            .ib-dot  { animation: ib-pulse 2.4s ease-in-out infinite; }
          `;
          document.head.appendChild(st);
        }

        const [ix, iy] = ibadan;
        const g = svg.append('g');
        g.append('circle').attr('cx', ix).attr('cy', iy).attr('r', 5)
          .attr('fill', 'none').attr('stroke', '#c94a00').attr('class', 'ib-rip1');
        g.append('circle').attr('cx', ix).attr('cy', iy).attr('r', 5)
          .attr('fill', 'none').attr('stroke', '#c94a00').attr('class', 'ib-rip2');
        g.append('circle').attr('cx', ix).attr('cy', iy).attr('r', 5)
          .attr('fill', '#c94a00').attr('class', 'ib-dot');
        svg.append('text').attr('x', ix + 9).attr('y', iy + 4)
          .attr('font-size', 9.5).attr('font-weight', '700')
          .attr('fill', '#8b3a00').attr('font-family', 'sans-serif').text('Ibadan, NG');

      } catch (e) { console.warn('WorldMap error:', e); }
    })();
  }, []);

  return (
    <section className="hp2-map-section" aria-hidden="true">
      <div className="hp2-map-label">
        <span className="hp2-map-badge">Our Reach</span>
        <p>Students from Ibadan connecting with alumni and learners across the globe</p>
      </div>
      <div ref={mapRef} style={{ width: '100%', lineHeight: 0 }} />
    </section>
  );
}

/* ─── static data ────────────────────────────────────────────────────────── */
const TICKER_FALLBACK = ['Chemistry','Physics','Mathematics','Biology','Biochemistry','Statistics','Zoology','Botany','Microbiology','Computer Science','Physiology','Anatomy'];

const FEATURES = [
  { num: '01', title: 'Curriculum-Aligned Teaching',  body: 'Our tutors study the exact UI course outlines so every session is directly relevant to your exams, tests, and practical work.' },
  { num: '02', title: 'Small Group Sessions',         body: 'We keep groups intimate so every student gets personal attention. No student slips through the cracks.' },
  { num: '03', title: 'Past Question Mastery',        body: 'Extensive past question drilling helps students recognise patterns, build speed, and walk into exams with confidence.' },
  { num: '04', title: 'Flexible Scheduling',          body: "Sessions timed around the academic calendar — pre-test week, semester coverage, or intensive crash prep. We're there." },
];

const HOW_IT_WORKS = [
  {
    step: '01', title: 'Sign Up & Choose Your Courses',
    body: 'Create your student account, browse the course catalogue, and enroll in the subjects you need help with.',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  },
  {
    step: '02', title: 'Attend Live & Recorded Sessions',
    body: 'Join scheduled tutorial sessions or catch up anytime with recordings. Ask questions, share notes, and collaborate with peers.',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
  },
  {
    step: '03', title: 'Test Yourself & Track Progress',
    body: 'Take quizzes, attempt past questions, and watch your performance improve with our in-depth analytics dashboard.',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  },
];

const SAMPLE_COURSES = [
  { code: 'CHM 101', title: 'Introductory Chemistry I',  level: '100 Level' },
  { code: 'PHY 102', title: 'General Physics II',        level: '100 Level' },
  { code: 'MTH 101', title: 'Elementary Mathematics',    level: '100 Level' },
  { code: 'CHM 203', title: 'Organic Chemistry I',       level: '200 Level' },
  { code: 'BCH 201', title: 'Introductory Biochemistry', level: '200 Level' },
  { code: 'STA 101', title: 'Probability & Statistics',  level: '100 Level' },
  { code: 'ZOO 201', title: 'General Zoology',           level: '200 Level' },
  { code: 'PHY 301', title: 'Classical Mechanics',       level: '300 Level' },
];

const STATS_FALLBACK = [
  { key: 'students', num: '500+', label: 'Students Taught',  fill: 82 },
  { key: 'courses',  num: '40+',  label: 'Courses Covered',  fill: 70 },
  { key: 'passRate', num: '95%',  label: 'Pass Rate',        fill: 95 },
  { key: 'tutors',   num: '10+',  label: 'Years Active',     fill: 60 },
];

// Faculty pills shown under the hero copy (mirrors the JAMB landing subject pills)
const HERO_PILLS = ['Chemistry', 'Physics', 'Mathematics', 'Biology', 'Biochemistry', '100L–500L'];

const DEFAULT_TESTIMONIALS = [
  { text: 'Toppers changed everything. I was struggling with CHM 203 — after just three weeks with them I not only passed, I came out with an A. I refer every science student I know.', name: 'Chukwuemeka A.', dept: 'Chemistry, 200L · UI' },
  { text: "The tutors explain things in a way that finally clicks. I've tried other tutorials before Toppers — nothing compared.", name: 'Blessing N.', dept: 'Biochemistry, 300L · UI' },
  { text: "The quiz practice sessions and past question bank are incredible. I felt prepared walking into every exam.", name: 'Tolu M.', dept: 'Physics, 300L · UI' },
];

const FAQ_ITEMS = [
  { q: 'Who is Toppers Tutorial for?', a: 'University of Ibadan science undergraduates from 100 to 500 level across all science faculties — Chemistry, Physics, Mathematics, Biology, Biochemistry, and more.' },
  { q: 'How do I enroll in a course?', a: 'Sign up on the platform, browse the course catalogue, and click Enroll. Your assigned tutor will reach out within 24 hours to confirm your session schedule.' },
  { q: 'Are sessions online or in person?', a: 'Both. We offer live online sessions via our platform and in-person tutorials at the University of Ibadan campus. You pick what works best for you.' },
  { q: 'Can I access past session recordings?', a: 'Yes. Every recorded session is available on your dashboard at any time, so you never fall behind even if you miss a live class.' },
  { q: 'How are tutors selected?', a: 'Our tutors are high-achieving UI graduates and postgrads with proven track records. Every tutor goes through a vetting process before joining the platform.' },
  { q: 'What happens if I need help outside sessions?', a: 'Students have access to a peer-and-tutor discussion forum, direct messaging, and a curated past question bank — so support is always available.' },
];

const BENEFITS = [
  { title: 'Built Around the UI Calendar', body: 'Every session maps to the University of Ibadan academic timetable — pre-test week, mid-semester CAs, and final exam coverage are planned ahead so you are never caught off guard.' },
  { title: 'Tutors Who Sat the Same Exams', body: 'Your mentors are high-achieving UI undergraduates and graduates who recently topped the very courses you are taking. They teach from real, recent experience.' },
  { title: 'A Real Past-Question Bank', body: 'Years of curated UI past questions with worked solutions, sorted by course and topic, so you drill the exact patterns examiners reuse year after year.' },
  { title: 'Live Performance Analytics', body: 'A personal dashboard tracks every quiz, CA, and mock so you always know which topics are solid and which need one more pass before the exam.' },
  { title: 'Both Online and On Campus', body: 'Join live online sessions from your hostel or attend in-person tutorials on campus — and replay every recording whenever revision calls.' },
  { title: 'A Community That Pulls You Up', body: 'Study groups, peer discussion forums, and direct access to tutors mean you are surrounded by students who are just as serious about excelling as you are.' },
];

const IMPACT_BAND = [
  { num: '12,000+', label: 'Tutorial hours delivered' },
  { num: '6', label: 'Science faculties served' },
  { num: '4.1 → 4.6', label: 'Average CGPA lift' },
  { num: '24h', label: 'Tutor response time' },
];

const COMMITMENTS = [
  { tag: 'Clarity', title: 'Concepts explained until they click', body: 'We do not rush past hard topics. Tutors break ideas down, use UI-relevant examples, and stay until the room actually understands.' },
  { tag: 'Consistency', title: 'Structured coverage, every week', body: 'No scattered, last-minute cramming. Each course follows a planned arc that mirrors how your lecturers move through the syllabus.' },
  { tag: 'Accountability', title: 'Progress you can see and measure', body: 'Regular CAs and mocks keep you honest about where you stand, with clear corrections that turn every mistake into a lesson.' },
];

const HERO_PHRASES = [
  'Where Science Students Thrive',
  'Your Academic Edge Starts Here',
  'Expert-Led. Results-Driven.',
  'Built for University of Ibadan',
  'From Struggle to Success',
];

/* ─── org chart ──────────────────────────────────────────────────────────── */
const ROLE_TIERS = ['Founder', 'Co-Founder', 'Senior Tutor', 'Tutor', 'Junior Tutor'];
function tierOf(role) {
  const idx = ROLE_TIERS.findIndex(r => role?.toLowerCase().includes(r.toLowerCase()));
  return idx === -1 ? ROLE_TIERS.length : idx;
}
function OrgChart({ members }) {
  const groups = {};
  members.forEach(m => {
    const t = tierOf(m.role);
    if (!groups[t]) groups[t] = [];
    groups[t].push(m);
  });
  const tiers = Object.keys(groups).map(Number).sort((a, b) => a - b);
  if (tiers.length < 2) return null;
  return (
    <div className="hp2-org">
      <div className="hp2-org-title">Organisational Structure</div>
      <div className="hp2-org-chart">
        {tiers.map((t, ti) => (
          <div key={t} className="hp2-org-tier">
            {ti > 0 && <div className="hp2-org-connector" />}
            <div className="hp2-org-tier-nodes">
              {groups[t].map(m => (
                <Link key={m._id} to={`/team/${m._id}`} className="hp2-org-node">
                  <div className="hp2-org-avatar">
                    {m.photo
                      ? <img src={m.photo} alt={m.name} />
                      : <span>{m.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}</span>}
                  </div>
                  <div className="hp2-org-node-name">{m.name.split(' ')[0]}</div>
                  <div className="hp2-org-node-role">{m.role}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── main component ─────────────────────────────────────────────────────── */
export default function Homepage() {
  const navigate = useNavigate();
  const [content,         setContent]         = useState(null);
  const [apk,             setApk]             = useState(null);
  const [pubStats,        setPubStats]         = useState(null);
  const [menuOpen,        setMenuOpen]         = useState(false);
  const [heroImages,      setHeroImages]       = useState([]);
  const [showResultModal, setShowResultModal]  = useState(false);
  const [teamMembers,     setTeamMembers]      = useState([]);
  const [openFaq,         setOpenFaq]          = useState(null);
  const [newsArticles,    setNewsArticles]     = useState([]);

  const { display: typedText, phaseIdx } = useTypewriter(HERO_PHRASES);

  useScrollReveal();

  useEffect(() => {
    api.get('/admin/content').then(r => setContent(r.data.content)).catch(() => {});
    api.get('/hero-images').then(r => setHeroImages(r.data.images || [])).catch(() => {});
    getPlatformSettings().then(s => { if (s?.apkUrl) setApk({ url: s.apkUrl, version: s.apkVersion }); }).catch(() => {});
    api.get('/admin/team').then(r => setTeamMembers(r.data.members || [])).catch(() => {});
    api.get('/news', { params: { limit: 3, page: 1, status: 'published' } }).then(r => setNewsArticles(r.data.articles || [])).catch(() => {});
    api.get('/platform-settings/public-stats').then(r => setPubStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const h = () => { if (window.innerWidth > 768) setMenuOpen(false); };
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const testimonials = content?.testimonials?.length > 0 ? content.testimonials : DEFAULT_TESTIMONIALS;
  // Live counts win when present; admin marketing value is the middle
  // fallback; the hardcoded string is the last resort.
  const liveStat = (key) => {
    const map = { students: pubStats?.students, courses: pubStats?.courses, tutors: pubStats?.tutors };
    const v = map[key];
    if (!(v > 0)) return null;
    return key === 'students' && v >= 50 ? `${Math.floor(v / 50) * 50}+` : v.toLocaleString();
  };
  const stats = STATS_FALLBACK.map(s => ({ ...s, num: liveStat(s.key) || content?.stats?.[s.key] || s.num }));
  const tickerSource = content?.tickerCourses?.length > 0 ? content.tickerCourses : TICKER_FALLBACK;
  const howItWorksItems = content?.howItWorks?.length > 0 ? content.howItWorks : HOW_IT_WORKS;
  const faqItems = content?.faq?.length > 0 ? content.faq : FAQ_ITEMS;
  const tickerItems  = [...tickerSource, ...tickerSource];
  const currentHero  = heroImages[0]; // static hero — one calm image, no rotation
  const contact      = content?.contact || {};

  const contactItems = [
    contact.phone    ? { Icon: IcPhone,    label: 'Phone',     value: contact.phone }    : null,
    contact.address  ? { Icon: IcMapPin,   label: 'Location',  value: contact.address }  : null,
    contact.whatsapp ? { Icon: IcWhatsApp, label: 'WhatsApp',  value: contact.whatsapp } : null,
    contact.email    ? { Icon: IcMail,     label: 'Email',     value: contact.email }    : null,
  ].filter(Boolean);

  const fallbackContact = [
    { Icon: IcMail,     label: 'Email',    value: 'info@topperstutorial.com.ng' },
    { Icon: IcMapPin,   label: 'Location', value: 'University of Ibadan, Ibadan, Oyo State, Nigeria' },
    { Icon: IcWhatsApp, label: 'WhatsApp', value: 'Message us to get started' },
  ];

  // Accent color cycling per hero phrase
  const accentColors = ['var(--org)','#FF8A3D','#FF6B35','#FFA040','var(--org)'];
  const heroAccent = accentColors[phaseIdx % accentColors.length];

  return (
    <div className="hp2">
      {/* ── Nav ── */}
      <nav className="hp2-nav">
        <div className="hp2-logo" onClick={() => navigate('/')}>Toppers Tutorial <sup>UI</sup></div>
        <ul className={`hp2-nav-links${menuOpen ? ' open' : ''}`}>
          <li><a href="#hp2-how"      onClick={() => setMenuOpen(false)}>How It Works</a></li>
          <li><a href="#hp2-features" onClick={() => setMenuOpen(false)}>Why Us</a></li>
          <li><a href="#hp2-courses"  onClick={() => setMenuOpen(false)}>Courses</a></li>
          <li><a href="#hp2-team"     onClick={() => setMenuOpen(false)}>Our Team</a></li>
          <li><a href="#hp2-testi"    onClick={() => setMenuOpen(false)}>Students</a></li>
          <li><a href="#hp2-news"     onClick={() => setMenuOpen(false)}>News</a></li>
          <li><a href="#hp2-contact"  onClick={() => setMenuOpen(false)}>Contact</a></li>
          <li><a style={{ color: '#047A39', fontWeight: 700, cursor: 'pointer' }} onClick={() => { setMenuOpen(false); navigate('/jamb'); }}>JAMB Prep</a></li>
          <li className="hp2-mobile-cta">
            <button className="hp2-nav-ghost" onClick={() => { setMenuOpen(false); navigate('/signin'); }}>Sign In</button>
            <button className="hp2-nav-cta"   onClick={() => { setMenuOpen(false); navigate('/signup'); }}>Get Started</button>
          </li>
        </ul>
        <div className="hp2-nav-right">
          <button className="hp2-nav-ghost" onClick={() => navigate('/signin')}>Sign In</button>
          <button className="hp2-nav-cta"   onClick={() => navigate('/signup')}>Get Started</button>
        </div>
        <button className={`hp2-hamburger${menuOpen ? ' is-open' : ''}`} onClick={() => setMenuOpen(m => !m)} aria-label="Open menu">
          <span /><span /><span />
        </button>
      </nav>

      {menuOpen && <div className="hp2-menu-backdrop" onClick={() => setMenuOpen(false)} />}

      {/* ── Hero ── */}
      <section className="hp2-hero">
        {currentHero && <img key={currentHero._id} src={currentHero.url} alt="" className="hp2-hero-bg" />}
        {currentHero && <div className="hp2-hero-overlay" />}
        <div className="hp2-hero-layout">
          <div className="hp2-hero-inner">
            <div className="hp2-hero-enroll-block">
              <div className="hp2-hero-badge"><span className="hp2-hero-badge-dot" /> Now Enrolling</div>
              <div className="hp2-hero-session-pill">{content?.currentSession || '2025/2026'} Academic Session</div>
            </div>
            <div className="hp2-hero-typewriter-wrap">
              <h1 className="hp2-hero-typewriter">
                {typedText}
                <span className="hp2-tw-cursor">|</span>
              </h1>
              <div className="hp2-tw-underline" style={{ background: `linear-gradient(90deg, ${heroAccent}, transparent)` }} />
            </div>
            <p className="hp2-hero-sub">Academic excellence starts here. Toppers Tutorial offers expert-led support for University of Ibadan science undergraduates — every course, every level.</p>
            <div className="hp2-hero-actions">
              <button className="hp2-btn-primary" onClick={() => navigate('/signup')}>Enroll Today</button>
              <button className="hp2-btn-ghost" onClick={() => document.getElementById('hp2-how')?.scrollIntoView({ behavior: 'smooth' })}>How It Works</button>
            </div>
            <div className="hp2-hero-pills">
              {HERO_PILLS.map(p => <span key={p} className="hp2-hero-pill">{p}</span>)}
            </div>
            <div style={{ marginTop: 20, display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="hp2-result-link" onClick={() => setShowResultModal(true)}>Check My Result →</button>
              {apk?.url && (
                <a href={apk.url} download style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', background: '#FF6A00', color: '#fff', fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 10, boxShadow: '0 4px 14px rgba(255,106,0,0.3)' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download the App{apk.version ? ` (v${apk.version})` : ''}
                </a>
              )}
            </div>
          </div>

          {/* Stats panel — numbers from admin content (falls back to defaults) */}
          <div className="hp2-hero-panel">
            {stats.map(s => (
              <div key={s.key} className="hp2-hero-stat-card">
                <div className="hp2-hero-stat-num">{s.num}</div>
                <div className="hp2-hero-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="hp2-scroll-hint">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </section>

      {/* ── Ticker ── */}
      <div className="hp2-ticker" aria-hidden="true">
        <div className="hp2-ticker-inner">{tickerItems.map((item, i) => <span key={i}>{item}</span>)}</div>
      </div>

      {/* ── Trust band ── */}
      <section className="hp2-trust">
        {[
          { t: 'Expert UI Tutors', s: 'High-achieving undergraduates guiding every course' },
          { t: 'All Science Courses', s: '100L to final year — nothing left out' },
          { t: 'Past Questions & CAs', s: 'Drill real questions with full explanations' },
          { t: 'Track Your CGPA', s: 'Live analytics on every grade you earn' },
        ].map((b, i) => (
          <div key={i} className="hp2-trust-item" data-reveal="up" style={{ transitionDelay: `${i * 0.08}s` }}>
            <div className="hp2-trust-check">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div>
              <div className="hp2-trust-title">{b.t}</div>
              <div className="hp2-trust-sub">{b.s}</div>
            </div>
          </div>
        ))}
      </section>

      {/* ── Dual Path ── */}
      <section className="hp2-dual-path" id="hp2-path">
        <div className="hp2-dual-path__label" data-reveal="up">
          <div className="hp2-section-tag">Choose Your Track</div>
          <h2 className="hp2-dual-path__title">Where are you on your academic journey?</h2>
          <p className="hp2-dual-path__sub">Two separate ecosystems — each built precisely for where you are right now.</p>
        </div>
        <div className="hp2-dual-path__grid" data-reveal="up">

          {/* University track */}
          <div className="hp2-dual-path__card hp2-dual-path__card--uni">
            <div className="hp2-dual-path__card-tag">University Students</div>
            <h3 className="hp2-dual-path__card-title">Full university tutorial support</h3>
            <p className="hp2-dual-path__card-desc">
              Expert-led courses for every level and department at the University of Ibadan.
              Assignments, CAs, CGPA tracking, past questions, and more.
            </p>
            <ul className="hp2-dual-path__list">
              <li>All science courses — 100L to 600L</li>
              <li>CGPA tracker and grade analytics</li>
              <li>CA, quiz, and assignment portal</li>
              <li>Certificate of completion</li>
              <li>100L–final year tutors</li>
            </ul>
            <button className="hp2-dual-path__btn hp2-dual-path__btn--uni" onClick={() => navigate('/signup')}>
              Join as a University Student
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
          </div>

          {/* JAMB track */}
          <div className="hp2-dual-path__card hp2-dual-path__card--jamb">
            <div className="hp2-dual-path__card-tag hp2-dual-path__card-tag--jamb">JAMB &amp; PUTME Students</div>
            <h3 className="hp2-dual-path__card-title">Focused JAMB preparation</h3>
            <p className="hp2-dual-path__card-desc">
              A dedicated ecosystem for candidates targeting Physics, Chemistry, Biology,
              Use of English, and Mathematics — with structured CAs and past-question drills.
            </p>
            <ul className="hp2-dual-path__list">
              <li>5 core JAMB subjects</li>
              <li>Practice quiz with full explanations</li>
              <li>Structured CAs as your main grade</li>
              <li>100L university tutors as mentors</li>
              <li>Unique TPJ Tutorial ID</li>
            </ul>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="hp2-dual-path__btn hp2-dual-path__btn--jamb" onClick={() => navigate('/jamb')}>
                Explore JAMB Programme
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
              <button className="hp2-dual-path__btn hp2-dual-path__btn--jamb-ghost" onClick={() => navigate('/jamb/quiz')}>
                Try a Free Quiz
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="hp2-how" id="hp2-how">
        <div className="hp2-how-header" data-reveal="up">
          <div className="hp2-section-tag">Getting Started</div>
          <h2>Three steps to <em>academic success</em></h2>
          <p className="hp2-how-sub">Simple, structured, and built around your schedule.</p>
        </div>
        <div className="hp2-how-steps">
          {howItWorksItems.map((step, i) => (
            <div key={step.step || i} className="hp2-how-card" data-reveal="up" style={{ transitionDelay: `${i * 0.14}s` }}>
              {step.icon && <div className="hp2-how-icon">{step.icon}</div>}
              <div className="hp2-how-step-num">{step.step || String(i + 1).padStart(2, '0')}</div>
              <h3 className="hp2-how-title">{step.title}</h3>
              <p className="hp2-how-body">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── World Map ── */}
      <Deferred minHeight={320}><WorldMap /></Deferred>

      {/* ── Features ── */}
      <section className="hp2-features" id="hp2-features">
        <div className="hp2-features-label" data-reveal="left">
          <div className="hp2-section-no">01</div>
          <div className="hp2-section-tag">Why Toppers</div>
          <h2>Learning that<br /><em>actually works</em></h2>
        </div>
        <div className="hp2-features-list">
          {FEATURES.map((f, i) => (
            <div key={f.num} className="hp2-feature-row" data-reveal={i % 2 === 0 ? 'left' : 'right'} style={{ transitionDelay: `${i * 0.12}s` }}>
              <div className="hp2-feature-num">{f.num}</div>
              <div><div className="hp2-feature-title">{f.title}</div><div className="hp2-feature-body">{f.body}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Benefits grid ── */}
      <section className="hp2-benefits" id="hp2-benefits">
        <div className="hp2-benefits-header" data-reveal="up">
          <div className="hp2-section-tag hp2-centered">More reasons students stay</div>
          <h2>Everything you need to excel — <em>in one place</em></h2>
          <p className="hp2-benefits-sub">Toppers Tutorial is more than a class. It is a full academic support system built specifically for University of Ibadan science undergraduates.</p>
        </div>
        <div className="hp2-benefits-grid">
          {BENEFITS.map((b, i) => (
            <div key={b.title} className="hp2-benefit-card" data-reveal="up" style={{ transitionDelay: `${(i % 3) * 0.14}s` }}>
              <div className="hp2-benefit-check">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h3 className="hp2-benefit-title">{b.title}</h3>
              <p className="hp2-benefit-body">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Courses ── */}
      <section className="hp2-courses" id="hp2-courses">
        <div className="hp2-courses-header">
          <div><div className="hp2-section-tag">Our Subjects</div><h2>Courses We Cover</h2></div>
          <button className="hp2-courses-all" onClick={() => navigate('/signup')}>All Courses &#8594;</button>
        </div>
        <div className="hp2-courses-grid">
          {SAMPLE_COURSES.map((c, i) => (
            <div key={c.code} className="hp2-course-card" data-reveal="up" style={{ transitionDelay: `${i * 0.07}s` }}>
              <div className="hp2-course-code">{c.code}</div>
              <div className="hp2-course-title">{c.title}</div>
              <div className="hp2-course-dept">{c.level}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Impact band ── */}
      <section className="hp2-impact">
        <div className="hp2-impact-inner">
          {IMPACT_BAND.map((s, i) => (
            <div key={s.label} className="hp2-impact-item" data-reveal="up" style={{ transitionDelay: `${i * 0.14}s` }}>
              <div className="hp2-impact-num">{s.num}</div>
              <div className="hp2-impact-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Team ── */}
      {teamMembers.length > 0 && (
        <section className="hp2-team" id="hp2-team">
          <div className="hp2-team-header" data-reveal="up">
            <div className="hp2-section-tag hp2-centered">The People Behind Toppers</div>
            <h2>Meet Our Team</h2>
            <p className="hp2-team-sub">Passionate educators, high-achieving graduates, and dedicated mentors — here to help you succeed.</p>
          </div>
          <div className="hp2-team-grid">
            {teamMembers.map((m, i) => (
              <Link key={m._id} to={`/team/${m._id}`} className="hp2-team-card hp2-team-card--link" data-reveal="up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="hp2-team-photo">
                  {m.photo
                    ? <img src={m.photo} alt={m.name} />
                    : <div className="hp2-team-initials">{m.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}</div>}
                </div>
                <div className="hp2-team-role">{m.role}</div>
                <div className="hp2-team-name">{m.name}</div>
                {m.bio && <p className="hp2-team-bio">{m.bio}</p>}
                {m.courses?.length > 0 && (
                  <div className="hp2-team-courses">
                    {m.courses.slice(0,3).map(c => <span key={c} className="hp2-team-course-tag">{c}</span>)}
                  </div>
                )}
                <div className="hp2-team-cta">View Profile &rarr;</div>
              </Link>
            ))}
          </div>
          <Deferred minHeight={280}><OrgChart members={teamMembers} /></Deferred>
        </section>
      )}

      {/* ── Gallery ── */}
      {(content?.galleryPhotos || []).length > 0 && (
        <section className="hp2-gallery" id="hp2-gallery">
          <div className="hp2-gallery-header" data-reveal="up">
            <div className="hp2-section-tag hp2-centered">Life at Toppers</div>
            <h2>A Glimpse Inside</h2>
          </div>
          <div className="hp2-gallery-grid">
            {(content.galleryPhotos).map((p, i) => (
              <div key={i} className="hp2-gallery-item" data-reveal="up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <img src={p.url} alt={p.caption || 'Toppers Tutorial'} />
                {p.caption && <div className="hp2-gallery-caption">{p.caption}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Testimonials ── */}
      <section className="hp2-testi" id="hp2-testi">
        <div className="hp2-section-tag hp2-centered" data-reveal="up">Student Stories</div>
        <h2 data-reveal="up" style={{ transitionDelay: '0.1s' }}>Real results, real students</h2>
        <div className="hp2-testi-grid">
          {testimonials.slice(0, 3).map((t, i) => (
            <div key={i} className={`hp2-testi-card${i === 0 ? ' hp2-testi-dark' : ''}`} data-reveal="up" style={{ transitionDelay: `${i * 0.13}s` }}>
              <div className="hp2-testi-mark" aria-hidden="true">&ldquo;</div>
              <p className="hp2-testi-text">{t.text}</p>
              <div className="hp2-testi-name">{t.name || t.author}</div>
              {(t.dept || t.role) && <div className="hp2-testi-dept">{t.dept || t.role}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── News Hub ── */}
      {newsArticles.length > 0 && (
        <section className="hp2-news" id="hp2-news">
          <div className="hp2-news-header" data-reveal="up">
            <div className="hp2-section-tag hp2-centered">Latest Updates</div>
            <h2>From the News Hub</h2>
            <p className="hp2-news-sub">Campus buzz, study tips, and academic opportunities — stay in the know.</p>
          </div>
          <div className="hp2-news-grid">
            {newsArticles.map((article, i) => (
              <Link key={article._id} to={`/news/${article.slug}`} className="hp2-news-card" data-reveal="up" style={{ transitionDelay: `${i * 0.1}s` }}>
                {(article.coverImageUrl || article.coverImage)
                  ? <img src={article.coverImageUrl || article.coverImage} alt={article.title} className="hp2-news-card-img" />
                  : <div className="hp2-news-card-img-placeholder" />}
                <div className="hp2-news-card-body">
                  <span className="hp2-news-card-cat">{article.category}</span>
                  <h3 className="hp2-news-card-title">{article.title}</h3>
                  {article.excerpt && <p className="hp2-news-card-excerpt">{article.excerpt}</p>}
                  <div className="hp2-news-card-footer">
                    <span className="hp2-news-card-date">{fmtDate(article.publishedAt || article.createdAt)}</span>
                    <span className="hp2-news-read-more">Read →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="hp2-news-viewall" data-reveal="up">
            <Link to="/news" className="hp2-news-viewall-btn">View All News →</Link>
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      <section className="hp2-faq" id="hp2-faq">
        <div className="hp2-faq-header" data-reveal="up">
          <div className="hp2-section-tag hp2-centered">Got Questions?</div>
          <h2>Frequently Asked Questions</h2>
        </div>
        <div className="hp2-faq-list">
          {faqItems.map((item, i) => (
            <div key={i} data-reveal="up" style={{ transitionDelay: `${i * 0.07}s` }}>
              <div className={`hp2-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="hp2-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{item.q}</span>
                  <svg className="hp2-faq-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                <div className="hp2-faq-a-wrap" style={{ maxHeight: openFaq === i ? 300 : 0 }}>
                  <div className="hp2-faq-a">{item.a}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Contact ── */}
      {/* ── JAMB cross-link band ── */}
      <section style={{ background: '#047A39', padding: '34px 24px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 19, marginBottom: 4 }}>Preparing for JAMB or Post-UTME?</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13.5 }}>Practice quizzes, CBT mocks and admission tools — on our dedicated JAMB platform.</div>
          </div>
          <button onClick={() => navigate('/jamb')} style={{ background: '#fff', color: '#047A39', border: 'none', borderRadius: 10, padding: '12px 26px', fontSize: 14, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Visit Toppers JAMB →
          </button>
        </div>
      </section>

      <section className="hp2-contact" id="hp2-contact">
        <div className="hp2-section-tag hp2-centered" data-reveal="up">Get In Touch</div>
        <h2 data-reveal="up" style={{ transitionDelay: '0.08s' }}>We're here to help</h2>
        <p className="hp2-contact-sub" data-reveal="up" style={{ transitionDelay: '0.16s' }}>
          {contact.tagline || 'Have questions about enrollment or courses? Reach out to us directly.'}
        </p>
        <div className="hp2-contact-grid">
          {(contactItems.length > 0 ? contactItems : fallbackContact).map((item, i) => (
            <div key={i} className="hp2-contact-card" data-reveal="up" style={{ transitionDelay: `${i * 0.1}s` }}>
              <div className="hp2-contact-icon"><item.Icon size={22} /></div>
              <div className="hp2-contact-label">{item.label}</div>
              <div className="hp2-contact-value">{item.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Our Commitment ── */}
      <section className="hp2-commit" id="hp2-commit">
        <div className="hp2-commit-header" data-reveal="up">
          <div className="hp2-section-tag hp2-centered">Our Promise to You</div>
          <h2>How we help you <em>actually improve</em></h2>
          <p className="hp2-commit-sub">Three principles guide every session we run — because real grades come from real understanding, not luck.</p>
        </div>
        <div className="hp2-commit-grid">
          {COMMITMENTS.map((c, i) => (
            <div key={c.tag} className="hp2-commit-card" data-reveal="up" style={{ transitionDelay: `${i * 0.14}s` }}>
              <div className="hp2-commit-tag">{c.tag}</div>
              <h3 className="hp2-commit-title">{c.title}</h3>
              <p className="hp2-commit-body">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hp2-cta" id="hp2-cta" data-reveal="up">
        <div className="hp2-cta-inner">
          <div className="hp2-cta-tag">Ready to start?</div>
          <h2>Join hundreds of students already excelling</h2>
          <p>{content?.about?.body || 'Toppers Tutorial has helped University of Ibadan science students score higher, understand deeper, and walk into every exam with real confidence.'}</p>
          <div className="hp2-cta-actions">
            <button className="hp2-btn-white" onClick={() => navigate('/signup')}>Enroll Now</button>
            <button className="hp2-cta-ghost" onClick={() => document.getElementById('hp2-contact')?.scrollIntoView({ behavior: 'smooth' })}>Contact Us</button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="hp2-footer">
        <div className="hp2-footer-top">
          <div>
            <div className="hp2-footer-logo">Toppers Tutorial</div>
            <div className="hp2-footer-tagline">University of Ibadan · Science Tutorials</div>
          </div>
          <div className="hp2-footer-nav">
            <button onClick={() => document.getElementById('hp2-how')?.scrollIntoView({ behavior: 'smooth' })}>How It Works</button>
            <button onClick={() => document.getElementById('hp2-courses')?.scrollIntoView({ behavior: 'smooth' })}>Courses</button>
            <button onClick={() => document.getElementById('hp2-team')?.scrollIntoView({ behavior: 'smooth' })}>Our Team</button>
            <button onClick={() => document.getElementById('hp2-contact')?.scrollIntoView({ behavior: 'smooth' })}>Contact</button>
            <button onClick={() => navigate('/news')}>News Hub</button>
            <button onClick={() => navigate('/signin')}>Sign In</button>
            <button onClick={() => navigate('/signup')}>Sign Up</button>
          </div>
        </div>
        <div className="hp2-footer-bottom">
          <p>&copy; {new Date().getFullYear()} Toppers Tutorial · University of Ibadan, Nigeria</p>
          <div className="hp2-footer-legal">
            <Link to="/terms">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy">Privacy</Link>
          </div>
        </div>
      </footer>

      {showResultModal && <ResultCheckerModal currentSession={content?.currentSession} onClose={() => setShowResultModal(false)} />}
    </div>
  );
}
